/**
 * Asset path helpers shared by every asset search surface (spec 2026-10-10-02).
 *
 * The list endpoint with `asset_chain=true` returns the matches plus every
 * ancestor row (flagged `parent_chain`), so a client can rebuild the chain
 * from the building group down without extra requests.
 */

export interface PathAsset {
    uid: string
    name?: string | null
    parent_asset_uid?: string | null
}

/**
 * Ancestors of `asset`, root first. A missing intermediate row ends the walk
 * (the chain is simply shorter) and a cycle in `parent_asset_uid` terminates.
 */
export function ancestorChain<T extends PathAsset>(asset: PathAsset, byUid: Map<string, T>): T[] {
    const chain: T[] = []
    const seen = new Set<string>([asset.uid])
    let current = asset.parent_asset_uid ? byUid.get(asset.parent_asset_uid) : undefined
    while (current && !seen.has(current.uid)) {
        seen.add(current.uid)
        chain.unshift(current)
        current = current.parent_asset_uid ? byUid.get(current.parent_asset_uid) : undefined
    }
    return chain
}

/**
 * `"Chambre 1 (Capitul, 2e, App 222)"`: the stored name, then the ancestors
 * from the root down. A root asset (or a chain with no named ancestor) is the
 * bare name. Never derive a room name here: use the stored one.
 */
export function formatAssetWithPath<T extends PathAsset>(asset: PathAsset, byUid: Map<string, T>): string {
    const name = asset.name || asset.uid
    const names = ancestorChain(asset, byUid)
        .map((a) => a.name)
        .filter((n): n is string => Boolean(n))
    return names.length > 0 ? `${name} (${names.join(', ')})` : name
}

/**
 * Like `formatAssetWithPath`, minus the leading ancestors shared with a
 * reference asset. `sharedUids` is the reference's ancestor chain (root first,
 * uids only: `ancestorChain(reference, byUid).map((a) => a.uid)`). Only the
 * common prefix compared uid by uid is dropped, never names (two buildings can
 * share one). Nothing shared keeps the full path; everything shared leaves the
 * bare name.
 */
export function formatAssetWithPathRelativeTo<T extends PathAsset>(
    asset: PathAsset,
    byUid: Map<string, T>,
    sharedUids: readonly string[],
): string {
    const name = asset.name || asset.uid
    const chain = ancestorChain(asset, byUid)
    let common = 0
    while (common < chain.length && common < sharedUids.length && chain[common].uid === sharedUids[common]) common++
    const names = chain
        .slice(common)
        .map((a) => a.name)
        .filter((n): n is string => Boolean(n))
    return names.length > 0 ? `${name} (${names.join(', ')})` : name
}

/**
 * Rows of an `asset_chain=true` response that are real matches (the
 * `parent_chain` ones only complete the chain), each with its path label.
 */
export function matchesWithPath<T extends PathAsset & { parent_chain?: boolean | null }>(
    rows: T[],
    sharedUids: readonly string[] = [],
): Array<{ row: T; label: string; fullLabel: string }> {
    const byUid = new Map(rows.map((r) => [r.uid, r]))
    return rows
        .filter((r) => !r.parent_chain)
        .map((row) => {
            const fullLabel = formatAssetWithPath(row, byUid)
            return { row, fullLabel, label: sharedUids.length > 0 ? formatAssetWithPathRelativeTo(row, byUid, sharedUids) : fullLabel }
        })
}
